import { useRef } from 'react';
import { VectorMap } from '@react-jvectormap/core';
import { worldMill } from '@react-jvectormap/world';
import { Country } from '../database';
import { Syringe } from 'lucide-react';
import { usePassiveEventListeners } from '../hooks/usePassiveEventListeners';
import i18n from '../i18n/i18n';

interface MapProps {
    countries: Country[];
    onCountrySelect: (country: Country | null) => Promise<boolean>;
}

export const MapLoadingSpinner = () => (
    <div className="flex flex-col items-center justify-center h-full">
        <div className="animate-spin mb-2">
            <Syringe className="w-12 h-12 text-blue-600" />
        </div>
        <p className="text-gray-600 font-medium">Loading...</p>
    </div>
);

const colorMap = {
    safe: 0,
    neutral: 1,
    unsafe: 2
} as const;
export default function Map({ countries, onCountrySelect }: MapProps) {
    const currentTipRef = useRef<HTMLElement | null>(null);
    if (currentTipRef.current) currentTipRef.current.style.display = 'none';
    for (const code in worldMill.content.paths) {
        if (code in worldMill.content.paths) {
            const countryName = i18n.t(`countries.${code}`, { defaultValue: code });
            worldMill.content.paths[code as keyof typeof worldMill.content.paths].name = countryName;
        }
    }
    const mapContainerRef = useRef<HTMLDivElement>(null);

    const getRegionValues = () => {
        const result: Record<string, number> = {};
        for (const country of countries) {
            result[country.iso_code] = colorMap[country.safety ?? 'neutral'];
        }
        return result;
    };

    // usePassiveEventListeners(
    //     mapContainerRef,
    //     ['wheel', 'touchstart'],
    //     (e) => {
    //         const container = mapContainerRef.current;
    //         if (!container) return;

    //         if (container.contains(e.target as Node)) {
    //             e.preventDefault();
    //         }
    //     }
    // );
    return (
        <div className="h-[500px] w-full" ref={mapContainerRef}>
            <VectorMap
                map={worldMill}
                backgroundColor="white"
                zoomOnScroll={true}
                zoomMax={8}
                zoomMin={1}
                zoomStep={1.6}
                style={{
                    width: '100%',
                    height: '100%'
                }}
                regionStyle={{
                    initial: {
                        fill: '#e4e4e4',
                        stroke: '#fff',
                        strokeWidth: 0.5,
                        fillOpacity: 1
                    },
                    hover: {
                        fillOpacity: 0.8,
                        cursor: 'pointer'
                    },
                    selected: {
                        fillOpacity: 0.9
                    }
                }}
                series={{
                    regions: [{
                        attribute: 'fill',
                        values: getRegionValues(),
                        scale: ["#4ade80", "#FFD700", "#f87171"]
                    }]
                }}
                onRegionClick={async (_e, code) => {
                    const country = countries.find(country => country.iso_code === code) ?? null;
                    if (await onCountrySelect(country) && currentTipRef.current) {
                        currentTipRef.current.style.display = 'none';
                    }
                }}
                onRegionTipShow={(_e, tip) => {
                    currentTipRef.current = (tip as unknown as HTMLElement[])[0] || null;
                }}
            />
        </div>
    );
} 